import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import {
  createNamingProfileVersion,
  type NamingField,
  type NamingProfileAssignments,
  type NamingProfileCatalog,
  type NamingProfileComponent,
  type NamingProfileRef,
} from '../../utils/namingProfile.logic';
import { FIELD_STYLE, LABEL, ROW, SECTION } from './PropertiesEditorStyles';

type ScopeKind = 'default' | 'module' | 'row' | 'zone';
type ScopeOption = { id: string; label: string };

type NamingProfileEditorProps = {
  profiles?: NamingProfileCatalog;
  assignments?: NamingProfileAssignments;
  modules: ScopeOption[];
  rows: ScopeOption[];
  onChange: (patch: {
    namingProfiles: NamingProfileCatalog;
    namingAssignments: NamingProfileAssignments;
  }) => void;
};

const defaultComponents: NamingProfileComponent[] = [
  { kind: 'field', field: 'module', encoding: 'numeric' },
  { kind: 'literal', value: '/' },
  { kind: 'field', field: 'row', encoding: 'numeric', padding: 2 },
  { kind: 'literal', value: '/' },
  { kind: 'field', field: 'rack', encoding: 'numeric', padding: 2 },
];

function allRefs(profiles: NamingProfileCatalog): Array<{ key: string; ref: NamingProfileRef; label: string }> {
  return Object.values(profiles).flatMap(versions => versions.map(version => ({
    key: `${version.profileId}@${version.version}`,
    ref: { profileId: version.profileId, version: version.version },
    label: `${version.name} · ${version.profileId} v${version.version}`,
  })));
}

function refKey(ref?: NamingProfileRef): string {
  return ref ? `${ref.profileId}@${ref.version}` : '';
}

function parseRef(value: string): NamingProfileRef | null {
  const separator = value.lastIndexOf('@');
  if (separator < 1) return null;
  const version = Number(value.slice(separator + 1));
  return Number.isInteger(version) && version > 0
    ? { profileId: value.slice(0, separator), version }
    : null;
}

function scopeMapKey(scope: ScopeKind): 'moduleOverrides' | 'rowOverrides' | 'zoneOverrides' {
  return `${scope}Overrides` as 'moduleOverrides' | 'rowOverrides' | 'zoneOverrides';
}

export const NamingProfileEditor: React.FC<NamingProfileEditorProps> = ({
  profiles = {},
  assignments,
  modules,
  rows,
  onChange,
}) => {
  const versions = useMemo(() => allRefs(profiles), [profiles]);
  const firstRef = versions[0]?.ref;
  const initialRef = assignments?.default || firstRef;
  const initialVersion = initialRef ? profiles[initialRef.profileId]?.find(item => item.version === initialRef.version) : undefined;
  const [selectedVersion, setSelectedVersion] = useState(refKey(initialRef));
  const [profileId, setProfileId] = useState(initialVersion?.profileId || initialRef?.profileId || 'rack-address');
  const [profileName, setProfileName] = useState(initialVersion?.name || 'Rack address');
  const [components, setComponents] = useState<NamingProfileComponent[]>(initialVersion?.profile.components || defaultComponents);
  const [scope, setScope] = useState<ScopeKind>('default');
  const [scopeId, setScopeId] = useState('');
  const [assignmentValue, setAssignmentValue] = useState(refKey(initialRef));
  const [error, setError] = useState('');

  const loadVersion = (value: string) => {
    setSelectedVersion(value);
    const ref = parseRef(value);
    const version = ref && profiles[ref.profileId]?.find(item => item.version === ref.version);
    if (!version) return;
    setProfileId(version.profileId);
    setProfileName(version.name);
    setComponents(structuredClone(version.profile.components));
    setError('');
  };

  useEffect(() => {
    if (selectedVersion || !versions.length) return;
    const ref = assignments?.default || firstRef;
    if (!ref) return;
    loadVersion(refKey(ref));
    setAssignmentValue(refKey(ref));
  }, [assignments?.default, firstRef, profiles, selectedVersion, versions.length]);

  const getScopeAssignment = (nextScope: ScopeKind, nextScopeId: string): NamingProfileRef | undefined => {
    if (nextScope === 'default') return assignments?.default;
    const values = assignments?.[scopeMapKey(nextScope)];
    return nextScopeId ? values?.[nextScopeId] : undefined;
  };

  const changeScope = (nextScope: ScopeKind) => {
    setScope(nextScope);
    const nextId = '';
    setScopeId(nextId);
    const nextRef = getScopeAssignment(nextScope, nextId) || assignments?.default || firstRef;
    setAssignmentValue(refKey(nextRef));
  };

  const changeScopeId = (nextId: string) => {
    setScopeId(nextId);
    const nextRef = getScopeAssignment(scope, nextId) || assignments?.default || firstRef;
    setAssignmentValue(refKey(nextRef));
  };

  const updateComponent = (index: number, patch: Partial<NamingProfileComponent>) => {
    setComponents(current => current.map((component, componentIndex) => (
      componentIndex === index ? { ...component, ...patch } as NamingProfileComponent : component
    )));
  };

  const saveVersion = () => {
    try {
      const profileVersions = profiles[profileId] || [];
      const previous = profileVersions.reduce<typeof profileVersions[number] | undefined>(
        (latest, version) => !latest || version.version > latest.version ? version : latest,
        undefined,
      );
      const version = createNamingProfileVersion({
        profileId: profileId.trim(),
        name: profileName,
        profile: { components },
        previous,
      });
      const nextProfiles = {
        ...profiles,
        [version.profileId]: [...(profiles[version.profileId] || []), version],
      };
      const nextRef = { profileId: version.profileId, version: version.version };
      const nextAssignments: NamingProfileAssignments = {
        ...(assignments || {}),
        default: assignments?.default || nextRef,
      };
      if (scope === 'default') nextAssignments.default = nextRef;
      else if (scopeId) {
        const key = scopeMapKey(scope);
        nextAssignments[key] = { ...(nextAssignments[key] || {}), [scopeId]: nextRef };
      }
      setSelectedVersion(refKey(nextRef));
      setAssignmentValue(refKey(nextRef));
      onChange({ namingProfiles: nextProfiles, namingAssignments: nextAssignments });
      setError('');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Профиль не прошёл проверку');
    }
  };

  const saveAssignment = () => {
    const ref = parseRef(assignmentValue);
    if (!ref) return;
    const nextAssignments: NamingProfileAssignments = {
      ...(assignments || {}),
      default: assignments?.default || ref,
    };
    if (scope === 'default') nextAssignments.default = ref;
    else if (scopeId) {
      const key = scopeMapKey(scope);
      nextAssignments[key] = { ...(nextAssignments[key] || {}), [scopeId]: ref };
    } else {
      setError('Выберите объект области назначения');
      return;
    }
    onChange({ namingProfiles: profiles, namingAssignments: nextAssignments });
    setError('');
  };

  return (
    <>
      <div style={SECTION}>Профиль адресов</div>
      <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8 }}>
        Изменение сохраняется новой версией. Старые адреса не переписываются.
      </div>
      <div style={ROW}>
        <label style={LABEL}>Назначение</label>
        <select style={FIELD_STYLE} value={scope} onChange={event => changeScope(event.target.value as ScopeKind)}>
          <option value="default">ЦОД по умолчанию</option>
          <option value="module">Модуль</option>
          <option value="row">Ряд</option>
          <option value="zone">Зона</option>
        </select>
      </div>
      {scope !== 'default' && (
        <div style={ROW}>
          <label style={LABEL}>{scope === 'module' ? 'Модуль' : scope === 'row' ? 'Ряд' : 'ID зоны'}</label>
          {scope === 'zone' ? (
            <input style={FIELD_STYLE} value={scopeId} onChange={event => changeScopeId(event.target.value)} placeholder="zone-id" />
          ) : (
            <select style={FIELD_STYLE} value={scopeId} onChange={event => changeScopeId(event.target.value)}>
              <option value="">Выберите область</option>
              {(scope === 'module' ? modules : rows).map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          )}
        </div>
      )}
      <div style={ROW}>
        <label style={LABEL}>Версия области</label>
        <select style={FIELD_STYLE} value={assignmentValue} onChange={event => setAssignmentValue(event.target.value)} disabled={!versions.length}>
          {!versions.length && <option value="">Сначала создайте профиль</option>}
          {versions.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
        </select>
      </div>
      <button type="button" onClick={saveAssignment} disabled={!versions.length} style={{ ...FIELD_STYLE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: versions.length ? 'pointer' : 'not-allowed', marginBottom: 8 }}>
        <Save size={13} /> Сохранить назначение
      </button>

      <div style={{ borderTop: '1px solid #1f2937', paddingTop: 8, marginTop: 4 }}>
        <div style={{ ...SECTION, marginTop: 0 }}>Новая версия</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <input aria-label="ID профиля" style={FIELD_STYLE} value={profileId} onChange={event => setProfileId(event.target.value)} placeholder="profile-id" />
          <input aria-label="Название профиля" style={FIELD_STYLE} value={profileName} onChange={event => setProfileName(event.target.value)} placeholder="Название" />
        </div>
        <select aria-label="Загрузить версию" style={{ ...FIELD_STYLE, marginTop: 6 }} value={selectedVersion} onChange={event => loadVersion(event.target.value)} disabled={!versions.length}>
          {!versions.length && <option value="">Новый профиль</option>}
          {versions.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
        </select>
        <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
          {components.map((component, index) => (
            <div key={`${index}-${component.kind}`} style={{ display: 'grid', gridTemplateColumns: '74px 1fr 28px', gap: 5, alignItems: 'center' }}>
              <select aria-label={`Тип компонента ${index + 1}`} style={FIELD_STYLE} value={component.kind} onChange={event => updateComponent(index, event.target.value === 'literal' ? { kind: 'literal', value: '/' } : { kind: 'field', field: 'rack', encoding: 'numeric', padding: 2 })}>
                <option value="field">Поле</option>
                <option value="literal">Текст</option>
              </select>
              {component.kind === 'literal' ? (
                <input aria-label={`Текст компонента ${index + 1}`} style={FIELD_STYLE} value={component.value} onChange={event => updateComponent(index, { value: event.target.value })} />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  <select aria-label={`Поле компонента ${index + 1}`} style={FIELD_STYLE} value={component.field} onChange={event => updateComponent(index, { field: event.target.value as NamingField })}>
                    <option value="module">Модуль</option><option value="row">Ряд</option><option value="rack">Место</option>
                  </select>
                  <select aria-label={`Кодировка компонента ${index + 1}`} style={FIELD_STYLE} value={component.encoding} onChange={event => updateComponent(index, { encoding: event.target.value as 'numeric' | 'alpha' | 'dictionary' })}>
                    <option value="numeric">Число</option><option value="alpha">Буквы</option><option value="dictionary">Словарь</option>
                  </select>
                </div>
              )}
              <button type="button" aria-label={`Удалить компонент ${index + 1}`} title="Удалить компонент" onClick={() => setComponents(current => current.filter((_, componentIndex) => componentIndex !== index))} style={{ ...FIELD_STYLE, display: 'grid', placeItems: 'center', padding: 4, cursor: 'pointer' }}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
        {components.some(component => component.kind === 'field') && (
          <div style={{ display: 'grid', gap: 5, marginTop: 6 }}>
            {components.map((component, index) => component.kind === 'field' ? (
              <div key={`options-${index}`} style={{ display: 'grid', gridTemplateColumns: component.encoding === 'dictionary' ? '1fr 1fr 1fr 2fr' : '1fr 1fr 1fr', gap: 5 }}>
                <input aria-label={`Отступ компонента ${index + 1}`} style={FIELD_STYLE} type="number" min={1} placeholder="padding" value={component.padding ?? ''} onChange={event => updateComponent(index, { padding: event.target.value ? Number(event.target.value) : undefined })} />
                <input aria-label={`Множитель компонента ${index + 1}`} style={FIELD_STYLE} type="number" min={1} placeholder="multiplier" value={component.multiplier ?? ''} onChange={event => updateComponent(index, { multiplier: event.target.value ? Number(event.target.value) : undefined })} />
                <input aria-label={`Смещение компонента ${index + 1}`} style={FIELD_STYLE} type="number" placeholder="offset" value={component.offset ?? ''} onChange={event => updateComponent(index, { offset: event.target.value ? Number(event.target.value) : undefined })} />
                {component.encoding === 'dictionary' && <input aria-label={`Словарь компонента ${index + 1}`} style={FIELD_STYLE} placeholder={'{"1":"16H1"}'} value={JSON.stringify(component.dictionary ?? {})} onChange={event => {
                  try {
                    const dictionary = JSON.parse(event.target.value);
                    if (!dictionary || Array.isArray(dictionary) || typeof dictionary !== 'object') throw new Error('Словарь должен быть JSON-объектом');
                    updateComponent(index, { dictionary });
                    setError('');
                  } catch {
                    setError('Словарь должен быть JSON-объектом с уникальными значениями');
                  }
                }} />}
              </div>
            ) : null)}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button type="button" title="Добавить поле" onClick={() => setComponents(current => [...current, { kind: 'field', field: 'rack', encoding: 'numeric', padding: 2 }])} style={{ ...FIELD_STYLE, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}><Plus size={13} /> Поле</button>
          <button type="button" title="Добавить текст" onClick={() => setComponents(current => [...current, { kind: 'literal', value: '/' }])} style={{ ...FIELD_STYLE, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}><Plus size={13} /> Текст</button>
          <button type="button" onClick={saveVersion} style={{ ...FIELD_STYLE, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginInlineStart: 'auto' }}><Save size={13} /> Создать версию</button>
        </div>
        {error && <div role="alert" style={{ color: '#fca5a5', fontSize: 10, marginTop: 6 }}>{error}</div>}
      </div>
    </>
  );
};
